import * as React from 'react'
import { useCallback, useEffect, useRef, useState } from 'react'

import {
  FM_FORM_STATUSES,
  FormStatusCode,
} from '@user/store/agreements/typings'
import { getStatusFromStatusCode } from '@user/store/agreements/utils/getStatusFromStatusCode'
import { updateFormStatus } from '@user/store/agreements/slice'
import { useAppDispatch } from '@user/store/hooks/useAppDispatch'

interface Props {
  url: string
  onClose: () => void
  onStatusChange?: (status: FM_FORM_STATUSES | undefined) => void
}

export const EmbeddedForm = (props: Props) => {
  const { url, onClose, onStatusChange } = props
  const dispatch = useAppDispatch()
  const [isListening, setIsListening] = useState<boolean>(false)

  const mounted = useRef(false)
  useEffect(() => {
    mounted.current = true
    return () => {
      mounted.current = false
    }
  }, [])

  const formCallback = useCallback(
    (event: MessageEvent) => {
      if (!mounted.current || !url.startsWith(event.origin)) {
        return
      }

      const form: { formId: string; status: FormStatusCode } = JSON.parse(
        event.data,
      )

      if (form.formId) {
        const status = getStatusFromStatusCode(form.status)
        dispatch(
          updateFormStatus({
            formId: form.formId,
            status,
          }),
        )
        onStatusChange?.(status)

        // The customer just signed on the salesperson's device — keep the
        // sheet open; the salesperson closes it manually to take back over
        if (
          status === FM_FORM_STATUSES.Signed ||
          status === FM_FORM_STATUSES.Complete
        ) {
          return
        }
      }

      onClose()
    },
    [dispatch, url, onStatusChange],
  )

  useEffect(() => {
    if (mounted.current && !isListening) {
      window.addEventListener('message', formCallback, false)
      setIsListening(true)
    }

    return () => {
      if (!mounted.current) {
        window.removeEventListener('message', formCallback)
      }
    }
  }, [formCallback, isListening])

  return <iframe src={url} height="100%" width="100%" frameBorder="0"></iframe>
}

EmbeddedForm.displayName = 'EmbeddedForm'
